import { defer, type LoaderFunctionArgs } from "@shopify/remix-oxygen";
import {
  queryMembersDynamic,
  queryPage,
  queryPartnersAll,
  queryResearchDynamic,
  queryFeaturedResearchDynamic,
} from "~/queries/page";
import Route from "~/components/routes/Route";
import {
  QueryResearchStaticResult,
  QueryPageResult,
  QueryMembersStaticResult,
} from "~/sanity/types";
import {
  getSearchParamPage,
  getSearchParamRegion,
  getSearchParamYear,
  getSlug,
} from "~/utils";
// @ts-ignore
import { getGravityForm } from "next-gravity-forms/server";
import { useLoaderData } from "@remix-run/react";

export type LoaderDataPage = ReturnType<typeof useLoaderData<typeof loader>>;
import { getSeoMeta } from "@shopify/hydrogen";
import { MetaArgs } from "@remix-run/react";
import { urlFor } from "~/components/elements/Image";
import { LoaderDataApiStats } from "./api/stats";
import { loader as loaderStats } from "~/data/loaders/api/stats";

export const meta = ({ matches }: MetaArgs<typeof loader>) => {
  return getSeoMeta(...matches.map((match) => (match.data as any).seo));
};

export async function loader(args: LoaderFunctionArgs) {
  const { request, params, context } = args;
  const { sanity } = context;
  const slug = getSlug({ params });

  let template;
  let formData;
  let research;
  let featuredResearch;
  let status;
  let members;
  let membersCarousel;
  let partners;
  let industry = context.cloudflare.env.SITE_THEME || null;
  let trainingStats;

  let page = await sanity.loadQuery<QueryPageResult>(queryPage, {
    slug: slug,
  });

  // If the page isn't available
  if (!page.data || !page.data.template) {
    // Check if page is a draft and are currently in preview mode
    if (
      (page.data?._originalId?.match(/^drafts\..*/) ||
        page.data?.template?._originalId?.match(/^drafts\..*/)) &&
      context.sanity.preview?.enabled
    ) {
      // If true tell user the page is under construction
      throw new Response("Under Construction", {
        status: 503,
      });
    } else {
      // Else throw 404 error
      page = await sanity.loadQuery<QueryPageResult>(queryPage, {
        slug: "404",
      });
      throw new Response(JSON.stringify(page.data), {
        status: 404,
      });
    }
  }

  if (page.data) {
    if (page.data.template && "sectionForm" in page.data.template) {
      const formId = page.data.template?.sectionForm?.formId;
      if (formId !== undefined) {
        formData = await getGravityForm(
          formId,
          context.cloudflare.env.WORDPRESS_API_URL,
        );
      }
    }

    // GET ALL RESEARCH PAPERS
    if (
      page.data.template?._type &&
      (page.data.template?._type as String) === "templateArchiveResearchLibrary"
    ) {
      const url = new URL(request.url);
      const searchParams = url.searchParams;
      const params = {
        pageIndex: getSearchParamPage(searchParams),
        year: getSearchParamYear(searchParams),
        region: getSearchParamRegion(searchParams),
      };

      research = (
        await sanity.loadQuery<QueryResearchStaticResult>(
          queryResearchDynamic,
          params,
        )
      ).data;
    }

    // GET ALL FEATURED RESEARCH PAPERS (Home page carousel)
    if (
      page.data.template?._type &&
      (page.data.template?._type as String) === "templatePageHome"
    ) {
      featuredResearch = (
        await sanity.loadQuery<any>(queryFeaturedResearchDynamic, {})
      ).data.items;
    }

    // GET ALL MEMBERS IN TEAM ARCHIVEå
    if (
      page.data.template?._type &&
      (page.data.template?._type as String) === "templateArchiveTeamLanding"
    ) {
      const url = new URL(request.url);
      const searchParams = url.searchParams;
      const params = {
        pageIndex: getSearchParamPage(searchParams),
        slug: page.data.template.teamArchive?.slug?.current || "the-mates-team",
      };

      members = (
        await sanity.loadQuery<QueryMembersStaticResult>(
          queryMembersDynamic,
          params,
        )
      ).data;

      // GET ALL MEMBERS FOR TEAM CAROUSEL (first page, no pagination UI)

      const carouselSlug =
        page.data.template.teamCarousel?.team?.slug?.current ||
        "the-mates-board";

      membersCarousel = (
        await sanity.loadQuery<QueryMembersStaticResult>(queryMembersDynamic, {
          pageIndex: 0,
          slug: carouselSlug,
        })
      ).data;
    }

    if (
      page.data.template?._type &&
      (page.data.template?._type as String) ===
        "templateArchiveIndustryPartners"
    ) {
      partners = (await sanity.loadQuery<any[]>(queryPartnersAll, {})).data;
    }

    // GET ALL STATS
    if (
      page.data.template?._type &&
      ((page.data.template?._type as String) === "templatePageHome" ||
        (page.data.template?._type as String) === "templatePageAbout")
    ) {
      const statsResponse = await loaderStats(args);
      trainingStats = (await statsResponse.json()) as LoaderDataApiStats;
    }
  }

  const seoImage = page.data?.seo?.image?.asset;
  const seo = {
    title: page.data?.title,
    ...page.data?.seo,
    media:
      seoImage &&
      urlFor({
        source: seoImage,
        sanityDataset: context.cloudflare.env.SANITY_DATASET,
        sanityProjectID: context.cloudflare.env.SANITY_PROJECT_ID,
      })
        .width(1200)
        .height(630)
        .url(),
  };

  const breadcrumbs = {
    currentPage: { slug: page.data?.slug, title: page.data?.title },
    parentPages: page.data?.parent,
  };

  return defer(
    {
      routeType: "page",
      page,
      form: {
        data: formData,
        baseUrl: context.cloudflare.env.WORDPRESS_API_URL,
      },
      research: research,
      members: members,
      membersCarousel: membersCarousel,
      partners,
      industry: industry,
      trainingStats: trainingStats,
      breadcrumbs,
      seo,
      featuredResearch,
    },
    // {
    // status: 200,
    //   headers: {
    //     // max-age: defines the time in seconds that the cache is valid (fresh) before going invalid (stale)
    //     // stale-while-revalidate: defines how long after the cache is marked stale it can be used for, provided it is being revalidated in the background
    //     // meaning that this cache is fresh for 30 seconds, and can be used for another 30 seconds while it is being revalidated, making the total cache duration 60 seconds
    //     "Cache-Control": "public, max-age=30, stale-while-revalidate=30",
    //     // "Cache-Control": "no-store",
    //   },
    // }
  );
}

export default function Page() {
  return <Route />;
}
